/* eslint-disable camelcase */
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Spinner,
  Stack,
  toast,
} from '@landler/component-library';
import i18next from 'i18next';
import { useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RestApiBadRequestServerError } from '@/api/rest/resources/errors/RestApiBadRequestError';
import { IndicatorFactType } from '@/api/rest/resources/types/fact';
import {
  DEFAULT_PLOT_GROUP_FILTERS,
  DEFAULT_PLOT_GROUP_GROUP_BY,
  PlotGroup,
  PlotGroupFilters,
  PlotGroupFiltersEnum,
  PlotGroupGroupBy,
  PlotGroupGroupByEnum,
  PlotLabelName,
} from '@/api/rest/resources/types/plotGroup';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { SuspenseBoundary } from '@/components';
import { usePlotGroupDefault } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { parsePlotGroupBodyData } from '@/components/PlotGroups/utils/parsePlotGroupBodyData';
import { usePlotGroupFiltersWidgets } from '@/components/widgets/config';
import { Card } from '@/components/widgets/o/Card/Card';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useDebounce } from '@/hooks/useDebounce';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { Logger } from '@/lib/logs/logger';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { useOrganizationPlotGroups } from '@/pages/shared/hooks/useOrganizationPlotGroup';
import {
  CreateEditGroupSubmitProps,
  useCreateGroup,
  usePlotGroupInvalidateQueries,
} from '@/pages/shared/hooks/usePlotGroup';
import { isNullish } from '@/utils/booleans';
import { squareMetersToHectares } from '@/utils/plot';

import { EditGroupFields } from '../../components/plot-group/EditPlotGroup';
import { useAvailableFilters } from '../../hooks/useAvailableFilters';
import { usePlotLabelNames } from '../../hooks/usePlotLabels';
import { useAllowedGraphTypes } from '../hooks/useAllowedGraphTypes';
import { SingleIndicatorCard } from './Indicators';

export type CompareFilterFields = EditGroupFields & { groupId: string | undefined; indicators: string[] };

export const CompareColumnHeader = ({
  form,
  group,
  columnKey,
}: {
  form: UseFormReturn<CompareFilterFields>;
  group: PlotGroup | null;
  columnKey: string;
}) => {
  return (
    <>
      {!isNullish(group) && (
        <Accordion className='group/accordion flex flex-col px-0 py-0' collapsible type='single'>
          <AccordionItem value='filters'>
            <AccordionTrigger className='border border-neutral-black-12 rounded-2xl hover:bg-white-100 gap-4 py-0'>
              <GroupHeader key={`groups-${columnKey}`} group={group} columnKey={columnKey} />
            </AccordionTrigger>
            <AccordionContent className='px-0 py-0 pt-4'>
              <CompareFilters form={form} key={`filters-${columnKey}`} group={group} columnKey={columnKey} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      {isNullish(group) && (
        <CompareFilters form={form} key={`filters-${columnKey}`} group={group} columnKey={columnKey} />
      )}
    </>
  );
};

export const CompareColumns = ({
  leftForm,
  rightForm,
  leftGroup,
  rightGroup,
  indicators,
  onHideIndicator,
}: {
  leftForm: UseFormReturn<CompareFilterFields>;
  rightForm: UseFormReturn<CompareFilterFields>;
  leftGroup: PlotGroup | null;
  rightGroup: PlotGroup | null;
  onHideIndicator: (indicator: IndicatorFactType) => void;
  indicators?: IndicatorFactType[];
}) => {
  const allowedGraphTypes = useAllowedGraphTypes(indicators);

  return (
    <Stack className='gap-4 my-2'>
      {!isNullish(leftGroup) && !isNullish(rightGroup) && (
        <div data-pdf-page-header style={{ display: 'none' }}>
          <div className='grid grid-cols-2 gap-4 border-b border-divider pb-3'>
            <GroupHeader group={leftGroup} columnKey='left' />
            <GroupHeader group={rightGroup} columnKey='right' />
          </div>
        </div>
      )}

      <div data-pdf-page='header' className='grid grid-cols-2 gap-4'>
        <SuspenseBoundary
          fallback={
            <Stack className='items-center justify-center w-full self-start'>
              <Spinner size={10} />
            </Stack>
          }
        >
          <CompareColumnHeader key='left' columnKey='left' form={leftForm} group={leftGroup} />
        </SuspenseBoundary>
        <SuspenseBoundary
          fallback={
            <Stack className='items-center justify-center w-full self-start'>
              <Spinner size={10} />
            </Stack>
          }
        >
          <CompareColumnHeader key='right' columnKey='right' form={rightForm} group={rightGroup} />
        </SuspenseBoundary>
      </div>

      {!isNullish(leftGroup) &&
        !isNullish(rightGroup) &&
        allowedGraphTypes.map((graphTileType, idx) => (
          <div key={graphTileType} data-pdf-page={`indicator-${idx}`} className='grid grid-cols-2 gap-4'>
            <SingleIndicatorCard
              graphTileType={graphTileType}
              group={leftGroup}
              columnKey='left'
              index={idx}
              onHideIndicator={onHideIndicator}
            />
            <SingleIndicatorCard
              graphTileType={graphTileType}
              group={rightGroup}
              columnKey='right'
              index={idx}
              onHideIndicator={onHideIndicator}
            />
          </div>
        ))}
    </Stack>
  );
};

export const GroupHeader = ({ group, columnKey }: { group: PlotGroup; columnKey: string }) => {
  const { t } = useTranslation();

  return (
    <Stack className='flex flex-row justify-between w-full' key={`groups-${columnKey}`}>
      <Card
        header={t('global.compare.group')}
        body={group?.name ?? ''}
        styles={{
          header: 'typography-overline text-text-secondary border-none py-0 pt-4',
          root: 'border-none rounded-2xl justify-start items-start',
        }}
      />
      <Card
        header={t('global.widget.totalSize')}
        body={
          group && !isNullish(group.total_area)
            ? `${getDisplayNumber(squareMetersToHectares(group.total_area as number), i18next.language) as string} ha`
            : '-'
        }
        styles={{
          header: 'typography-overline text-text-secondary border-none py-0 pt-4',
          root: 'border-none rounded-2xl justify-start items-start',
        }}
      />
    </Stack>
  );
};

export const CompareFilters = ({
  form,
  group,
  columnKey,
}: {
  form: UseFormReturn<CompareFilterFields>;
  group: PlotGroup | null;
  columnKey: string;
}) => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId();
  const plotGroupBodies = useOrganizationPlotGroups(organizationId).data;
  const plotGroups: PlotGroup[] = plotGroupBodies.map((body) => parsePlotGroupBodyData(body));

  const { submit } = useCreateGroup();
  const { invalidatePlotGroupRelatedQueries } = usePlotGroupInvalidateQueries();

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  const filteredPlotGroups = useMemo(() => {
    if (isNullish(debouncedSearchQuery.trim())) return plotGroups;

    const queryString = debouncedSearchQuery.toLowerCase();

    return plotGroups.filter((g) => g.name?.toLowerCase().includes(queryString));
  }, [plotGroups, debouncedSearchQuery]);

  const defaultPlotGroup = usePlotGroupDefault('default');
  const { data: availableFiltersData } = useAvailableFilters();
  const {
    available_country_codes = [],
    available_crops = [],
    available_land_types = [],
    has_deforestation = false,
  } = availableFiltersData ?? {};
  const availablePlotLabels: PlotLabelName[] = usePlotLabelNames().data ?? [];

  const defaultValues = {
    groupId: group?.id,
    name: group?.name ?? '',
    additional_plot_ids: group?.additional_plot_ids || [],
    ...(group?.filters ?? DEFAULT_PLOT_GROUP_FILTERS),
    ...(group?.groupBy ?? DEFAULT_PLOT_GROUP_GROUP_BY),
  };

  const onSuccess = (updatedGroup: PlotGroup) => {
    invalidatePlotGroupRelatedQueries(organizationId);
    if (updatedGroup.id) {
      form.setValue('groupId', updatedGroup.id);
    }
  };

  const onSubmit = async (
    input: EditGroupFields & PlotGroupFilters & PlotGroupGroupBy & { additional_plot_ids?: string[] },
  ) => {
    try {
      form.clearErrors();

      const filters = Object.fromEntries(
        Object.entries(input).filter(([key]) => Object.keys(PlotGroupFiltersEnum).includes(key)),
      ) as PlotGroupFilters;

      const groupBy = Object.fromEntries(
        Object.entries(input).filter(([key]) => Object.keys(PlotGroupGroupByEnum).includes(key)),
      ) as PlotGroupGroupBy;

      const { additional_plot_ids } = input;

      const formattedGroup: PlotGroup = {
        ...(group ?? defaultPlotGroup),
        filters,
        groupBy,
        additional_plot_ids,
      };

      const updatedGroup = await submit({
        organizationId,
        plotGroup: {
          ...formattedGroup,
          name: input.name ?? group?.name,
        },
      } as CreateEditGroupSubmitProps);
      onSuccess(parsePlotGroupBodyData(updatedGroup));
    } catch (error) {
      if (error instanceof Error) {
        Logger.error(error);
      }
      if (error instanceof RestApiBadRequestServerError && error.formErrors.length > 0) {
        error.formErrors.forEach((formError) => {
          form.setError(formError.field as keyof EditGroupFields, {
            type: 'custom',
            message: formError.messages?.[0],
          });
        });
      } else {
        toast({
          title: t('global.ui.toast.errorToastFallbackTitle'),
          description: t('global.ui.toast.errorToastFallbackDescription'),
          type: 'error',
        });
      }
    }
  };

  const filters = usePlotGroupFiltersWidgets({
    availablePlotLabels,
    available_country_codes,
    available_land_types,
    available_crops,
    has_deforestation,
  });

  const defaultWidgetSlotsConfiguration = {
    name: 'default_compare_filters',
    type: WidgetType.a_stack,
    visible: true,
    widgets: [
      {
        name: 'compare_filters',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          direction: 'row',
          center: true,
          spacing: 6,
          className: 'flex-col items-stretch',
        },
        widgets: [
          {
            name: 'compare_filters_plot_group_filters',
            type: WidgetType.o_form,
            visible: true,
            relatedData: {
              id: `compare-filters-form-${columnKey}`,
              key: `compare-filters-form-${group?.id}`,
              defaultValues,
              onSubmit,
              controlsFormArg: form,
            },
            widgets: [
              {
                name: 'compare_filters_plot_group_filters_section',
                type: WidgetType.o_form_section,
                visible: true,
                relatedData: {
                  previewChildrenLimit: 5,
                  spacing: 2,
                  className: 'py-4',
                  label: {
                    text: t('global.compare.filters.plotGroups.label'),
                    className: 'typography-h4 py-2 self-center',
                  },
                  section: {
                    className: 'w-full flex-wrap justify-start items-center flex-row',
                    centerMain: true,
                    direction: 'row',
                    spacing: 2,
                  },
                  formName: 'groupId',
                },
                widgets: [
                  {
                    name: 'compare_filters_plot_groups_search_bar',
                    visible: true,
                    type: WidgetType.m_search_bar,
                    relatedData: {
                      searchQuery,
                      setSearchQuery,
                      placeholder: t('global.compare.filters.plotGroups.search.placeholder'),
                      styles: {
                        container: 'w-full mb-1',
                      },
                    },
                  },
                  {
                    name: 'compare_filters_plot_group_filters_buttons',
                    visible: true,
                    type: WidgetType.o_form_button,
                    relatedData: {
                      data: filteredPlotGroups
                        .sort((a: PlotGroup, b: PlotGroup) => a.name?.localeCompare(b.name ?? '') || 0)
                        .map((pg: PlotGroup) => ({
                          text: pg.name,
                          formName: 'groupId',
                          value: pg.id,
                        })),
                    },
                    repeat: {
                      data: 'relatedData.data',
                      key: 'value',
                      template: {
                        name: 'compare_filters_plot_group_filters_button',
                        type: WidgetType.o_form_button,
                        visible: true,
                        relatedData: {
                          preventMultipleSelection: true,
                          variant: 'outline',
                          className: 'w-fit whitespace-nowrap text-text-primary font-normal',
                        },
                      },
                    },
                  },
                ],
              },
              {
                name: 'compare_filters_plot_group_divider',
                type: WidgetType.a_stack,
                visible: true,
                relatedData: {
                  className: 'flex flex-row items-center justify-center gap-2 py-4',
                },
                widgets: [
                  {
                    name: 'compare_filters_plot_group_divider_left_divider',
                    type: WidgetType.a_stack,
                    visible: true,
                    relatedData: {
                      className: 'border-b-2 border-text-primary h-1 w-full',
                    },
                  },
                  {
                    name: 'compare_filters_plot_group_divider_text',
                    type: WidgetType.a_text,
                    visible: true,
                    relatedData: {
                      text: t('global.compare.or'),
                      className: 'typography-h4 uppercase px-2',
                    },
                  },
                  {
                    name: 'compare_filters_plot_group_divider_right_divider',
                    type: WidgetType.a_stack,
                    visible: true,
                    relatedData: {
                      className: 'border-b-2 border-text-primary h-1 w-full',
                    },
                  },
                ],
              },
              {
                name: 'compare_filters_plot_group_create_group',
                type: WidgetType.a_stack,
                visible: true,
                relatedData: {
                  className: 'flex flex-row items-center justify-center',
                },
                widgets: [
                  {
                    name: `compare_filters_plot_group_create_group_toggle`,
                    type: WidgetType.a_text,
                    visible: true,
                    relatedData: {
                      className: 'typography-h4',
                      text: t('global.organization.plotGroup.createPlotGroup.createNewGroup'),
                    },
                  },
                ],
              },
              {
                name: 'compare_filters_group_name_section',
                type: WidgetType.o_form_section,
                visible: true,
                relatedData: {
                  className: 'py-4 border-b border-divider',
                  spacing: 2,
                  label: {
                    text: `${t('global.organization.plotGroup.createPlotGroup.form.name.label')}*`,
                    className: 'uppercase typography-overline pb-2 font-semibold',
                  },
                  section: {
                    className: 'w-full flex-wrap',
                    centerMain: true,
                    direction: 'row',
                    spacing: 2,
                  },
                  formName: 'name',
                },
                widgets: [
                  {
                    name: 'compare_filters_group_name_input',
                    type: WidgetType.o_form_text_input,
                    visible: true,
                    relatedData: {
                      required: true,
                      formName: 'name',
                      placeholder:
                        group?.name ?? t('global.organization.plotGroup.createPlotGroup.form.name.placeholder'),
                      helper: t('global.organization.plotGroup.createPlotGroup.form.name.helper'),
                      value: group?.name ?? null,
                      className: 'min-w-full rounded-lg',
                    },
                  },
                ],
              },
              ...filters,
              {
                name: 'compare_filters_actions',
                type: WidgetType.a_stack,
                relatedData: {
                  className: 'w-full justify-end pt-4',
                  direction: 'row',
                  centerMain: true,
                  spacing: 4,
                },
                visible: true,
                widgets: [
                  {
                    name: 'compare_filters_cancel_button',
                    type: WidgetType.o_form_clear_button,
                    visible: true,
                    relatedData: {
                      text: t('global.ui.buttons.cancel'),
                      className: 'w-fit',
                      variant: 'outline',
                    },
                  },
                  {
                    name: 'compare_filters_submit_button',
                    type: WidgetType.o_form_submit_button,
                    visible: true,
                    relatedData: {
                      text: t('global.ui.buttons.save'),
                      className: 'w-fit',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  return (
    <Card className='rounded-2xl' data-testid={`compare-filters-form-${columnKey}`}>
      <WidgetSlotRenderer nestedWidget={defaultWidgetSlotsConfiguration} />
    </Card>
  );
};
